/**
 * The mission registry: what the Learn tab offers. cpg.computer's, not the founder's.
 *
 * Missions come from two places: the copy bundled here (so the tab works offline and on day one), and later the feed
 * cpg.computer's API serves from the starter's latest release, which replaces entries by id. Ticks are kept per
 * person on cpg.computer's API (see ticks.ts). Every mission is open; `order` is only a suggestion. A mission whose
 * feature isn't built yet carries `needs` and shows as "Coming soon".
 *
 * A mission picks one check kind from a short, fixed list (checks.ts), so most new missions are copy only. Mission
 * copy may name the stack ("dev server", "Cloudflare link"): this is where founders learn how it works.
 */
import { crashCourse } from './content/crash-course'
import { firstDay } from './content/first-day'
import { firstWeek } from './content/first-week'
import { later } from './content/later'
import type { LearnPage } from './page'
import type { Recipe } from './recipes'

export type Track = 'first-day' | 'first-week' | 'later' | 'crash-course'

/** How a mission's page knows it's done. Each runs in the browser; only the tick leaves the app. */
export type Check =
  /**
   * A value in src/learn/page.ts differs from the first one this browser saw (`undo`: it changed, then came back to
   * the first one). `from`: compare with the first value another mission saw (undo compares with "Edit this page").
   */
  | { kind: 'value'; key: keyof LearnPage; undo?: boolean; from?: string }
  /** The page is open on the preview address (…-preview.cpg.computer). */
  | { kind: 'host' }
  /** The live app runs the preview's newest version (GET /healthz against GET /api/dev/version). */
  | { kind: 'version' }
  /** The screen list grew since the mission opened. */
  | { kind: 'registry' }
  /** The app's own data answers with example: false (a real store is connected). */
  | { kind: 'app-data'; path: string }
  /** Something happened in the shell, e.g. the command palette was used to jump. */
  | { kind: 'ui'; event: 'palette-jump' }
  /** A yes or no from cpg.computer's API (a key exists, a screen is shared…). */
  | { kind: 'signal'; signal: string }
  /** The founder taps "I did it". */
  | { kind: 'self' }

export interface Mission {
  /** Stable forever: ticks are stored against it. Lowercase words with hyphens. */
  id: string
  /** Its place in the suggested order within its group, shown as "03" (or "C3" in the crash course). */
  order: number
  title: string
  track: Track
  /** `say`: a sentence for the agent (the default). `do`: something the founder does, like a step in the dashboard. */
  act?: 'say' | 'do'
  /** One sentence to say to the agent, word for word (or what to do, for missions done in the dashboard). */
  say: string
  /** What should happen, in a few short steps. */
  happens: string[]
  check: Check
  /** What the page watches, in plain words, while it waits. */
  watching: string
  /** If it doesn't work: each symptom, and the exact words to say next. The last is always "flag us". */
  ifNot: { symptom: string; say: string }[]
  /** How this page knows, and what is saved: plain words. */
  how: string
  /** The base skill the agent relies on (.claude/skills/<name>), shown on the page so the founder can read it. */
  skill?: string
  /** The recipe in the manual on app.cpg.computer (recipes.ts). */
  recipe?: Recipe
  /** Set while the mission waits on a feature: shows as "Coming soon", with this sentence. */
  needs?: string
  /** A short explainer above the mission, in plain markdown (Prose). Every crash-course lesson has one. */
  lesson?: string
  /** One line for lists: what the lesson covers. */
  summary?: string
}

export const TRACKS: { id: Exclude<Track, 'crash-course'>; title: string; sentence: string }[] = [
  { id: 'first-day', title: 'First day', sentence: 'The round trip: ask, see it change, make it live.' },
  { id: 'first-week', title: 'First week', sentence: 'Real work on real data.' },
  { id: 'later', title: 'Later', sentence: 'Running it as a team.' },
]

export const CRASH_COURSE = {
  id: 'crash-course' as const,
  title: 'Crash course',
  sentence: 'How the pieces work: git, your preview link, shipping, dev and prod, and the habits that keep an app safe.',
}

/**
 * Bundled missions: 20 in three tracks (board learn-r2), then the crash course. Every one is open; `order` is only a
 * suggestion. The copy lives in ./content, one file per track.
 */
export const missions: Mission[] = [...firstDay, ...firstWeek, ...later, ...crashCourse]

const isCrash = (m: Pick<Mission, 'track'>) => m.track === 'crash-course'

/** Every mission in the suggested order: the three tracks first, then the crash course. */
export function missionsInOrder(list: Mission[] = missions): Mission[] {
  return [...list].sort((a, b) => Number(isCrash(a)) - Number(isCrash(b)) || a.order - b.order)
}

/** The missions in the same group as `m` (the tracks, or the crash course), in order: for "Next". */
export function sameGroup(m: Pick<Mission, 'track'>, list: Mission[] = missions): Mission[] {
  return missionsInOrder(list).filter((x) => isCrash(x) === isCrash(m))
}

export function missionById(id: string, list: Mission[] = missions): Mission | undefined {
  return list.find((m) => m.id === id)
}

/** "03", or "C3" in the crash course. */
export function missionNumber(m: Pick<Mission, 'order' | 'track'>): string {
  return isCrash(m) ? `C${m.order}` : String(m.order).padStart(2, '0')
}
